import { create } from 'zustand';
import { useAuthSessionStore } from '../../auth/session-store';
import type { ApiClient, TeamMember, TeamResponse } from '../../../lib/api';

export const ACTIVE_WORKSPACE_STORAGE_KEY = 'claudafinil:active-workspace';

export type ActiveWorkspace =
  | { type: 'personal' }
  | { type: 'team'; teamId: string; teamName: string };

export interface TeamInvitePreview {
  inviteCode: string;
  teamId: string;
  teamName: string;
}

const PERSONAL_WORKSPACE: ActiveWorkspace = { type: 'personal' };

function parseWorkspace(value: unknown): ActiveWorkspace {
  if (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value as { type?: unknown }).type === 'team' &&
    'teamId' in value &&
    typeof (value as { teamId?: unknown }).teamId === 'string' &&
    'teamName' in value &&
    typeof (value as { teamName?: unknown }).teamName === 'string'
  ) {
    const workspace = value as { type: 'team'; teamId: string; teamName: string };
    return {
      type: 'team',
      teamId: workspace.teamId,
      teamName: workspace.teamName,
    };
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value as { type?: unknown }).type === 'personal'
  ) {
    return PERSONAL_WORKSPACE;
  }

  return PERSONAL_WORKSPACE;
}

function readWorkspaceFromSessionStorage(): ActiveWorkspace {
  if (typeof window === 'undefined') {
    return PERSONAL_WORKSPACE;
  }

  try {
    const raw = window.sessionStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY);
    if (!raw) {
      return PERSONAL_WORKSPACE;
    }

    return parseWorkspace(JSON.parse(raw));
  } catch {
    return PERSONAL_WORKSPACE;
  }
}

function writeWorkspaceToSessionStorage(workspace: ActiveWorkspace): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      ACTIVE_WORKSPACE_STORAGE_KEY,
      JSON.stringify(workspace),
    );
  } catch {
    // ignore
  }
}

let apiClient: ApiClient | null = null;

export function setTeamsApiClient(client: ApiClient | null): void {
  apiClient = client;
}

async function refreshAuthenticatedTeamMembership(
  client: ApiClient,
  team: TeamResponse,
): Promise<void> {
  const authRefreshResult = await client.getMe();
  if (authRefreshResult.success) {
    useAuthSessionStore.getState().mergeUserInfo(authRefreshResult.data);
    return;
  }

  const { user } = useAuthSessionStore.getState();
  const teams = Array.isArray(user?.teams) ? user.teams : [];
  const hasTeam = teams.some((candidate) => candidate.teamId === team.id);
  useAuthSessionStore.getState().setAuthState({
    isAuthenticated: true,
    user: {
      ...(user ?? {}),
      teams: hasTeam
        ? teams
        : [
            ...teams,
            {
              teamId: team.id,
              teamName: team.name,
              role: team.role,
              joinedAt: team.createdAt,
            },
          ],
    },
    subscription: useAuthSessionStore.getState().subscription,
  });
}

export interface TeamState {
  activeWorkspace: ActiveWorkspace;
  teamMembers: TeamMember[];
  isLoadingMembers: boolean;
  isCreatingTeam: boolean;
  createTeamError: string | null;
  isLookingUpTeamInvite: boolean;
  isJoiningTeam: boolean;
  joinTeamError: string | null;
}

export interface TeamActions {
  setActiveWorkspace: (workspace: ActiveWorkspace) => void;
  loadActiveWorkspace: () => void;
  createTeam: (name: string) => Promise<ActiveWorkspace | null>;
  lookupTeamInvite: (inviteCode: string) => Promise<TeamInvitePreview | null>;
  joinTeam: (inviteCode: string) => Promise<ActiveWorkspace | null>;
  fetchTeamMembers: (teamId: string) => Promise<void>;
  clearTeamMembers: () => void;
  clearCreateTeamError: () => void;
  clearJoinTeamError: () => void;
}

export type TeamStore = TeamState & TeamActions;

export const useTeamStore = create<TeamStore>((set, get) => ({
  activeWorkspace: readWorkspaceFromSessionStorage(),
  teamMembers: [],
  isLoadingMembers: false,
  isCreatingTeam: false,
  createTeamError: null,
  isLookingUpTeamInvite: false,
  isJoiningTeam: false,
  joinTeamError: null,

  setActiveWorkspace: (workspace) => {
    writeWorkspaceToSessionStorage(workspace);
    set({
      activeWorkspace: workspace,
      teamMembers: [],
    });
  },

  loadActiveWorkspace: () => {
    const workspace = readWorkspaceFromSessionStorage();
    set({
      activeWorkspace: workspace,
      teamMembers: [],
    });
  },

  createTeam: async (name) => {
    if (!apiClient) {
      set({ createTeamError: 'API client not configured', isCreatingTeam: false });
      return null;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      set({ createTeamError: 'Team name is required', isCreatingTeam: false });
      return null;
    }

    if (trimmedName.length > 50) {
      set({
        createTeamError: 'Team name must be 50 characters or less',
        isCreatingTeam: false,
      });
      return null;
    }

    set({ createTeamError: null, isCreatingTeam: true });
    const result = await apiClient.createTeam({ name: trimmedName });

    if (!result.success) {
      set({
        createTeamError: result.error.message ?? result.error.error,
        isCreatingTeam: false,
      });
      return null;
    }

    const { team } = result.data;
    const workspace: ActiveWorkspace = {
      type: 'team',
      teamId: team.id,
      teamName: team.name,
    };

    await refreshAuthenticatedTeamMembership(apiClient, team);

    set({ isCreatingTeam: false });
    return workspace;
  },

  lookupTeamInvite: async (inviteCode) => {
    if (!apiClient) {
      set({ joinTeamError: 'API client not configured', isLookingUpTeamInvite: false });
      return null;
    }

    const trimmedInviteCode = inviteCode.trim();
    if (!trimmedInviteCode) {
      set({ joinTeamError: 'Invite code is required', isLookingUpTeamInvite: false });
      return null;
    }

    set({ joinTeamError: null, isLookingUpTeamInvite: true });
    const result = await apiClient.lookupTeamByInviteCode(trimmedInviteCode);

    if (!result.success) {
      set({
        joinTeamError: result.error.message ?? result.error.error,
        isLookingUpTeamInvite: false,
      });
      return null;
    }

    set({ isLookingUpTeamInvite: false });
    return {
      inviteCode: trimmedInviteCode,
      teamId: result.data.teamId,
      teamName: result.data.teamName,
    };
  },

  joinTeam: async (inviteCode) => {
    if (!apiClient) {
      set({ joinTeamError: 'API client not configured', isJoiningTeam: false });
      return null;
    }

    const trimmedInviteCode = inviteCode.trim();
    if (!trimmedInviteCode) {
      set({ joinTeamError: 'Invite code is required', isJoiningTeam: false });
      return null;
    }

    set({ joinTeamError: null, isJoiningTeam: true });
    const result = await apiClient.joinTeam(trimmedInviteCode);

    if (!result.success) {
      set({
        joinTeamError: result.error.message ?? result.error.error,
        isJoiningTeam: false,
      });
      return null;
    }

    const { team } = result.data;
    const workspace: ActiveWorkspace = {
      type: 'team',
      teamId: team.id,
      teamName: team.name,
    };

    await refreshAuthenticatedTeamMembership(apiClient, team);

    set({ isJoiningTeam: false });
    return workspace;
  },

  fetchTeamMembers: async (teamId) => {
    if (!apiClient) {
      set({ teamMembers: [], isLoadingMembers: false });
      return;
    }

    set({ isLoadingMembers: true });
    const result = await apiClient.getTeamMembers(teamId);

    if (!result.success) {
      set({ teamMembers: [], isLoadingMembers: false });
      return;
    }

    const activeWorkspace = get().activeWorkspace;
    if (activeWorkspace.type === 'team' && activeWorkspace.teamId === teamId) {
      set({ teamMembers: result.data.members, isLoadingMembers: false });
      return;
    }

    set({ isLoadingMembers: false });
  },

  clearTeamMembers: () => {
    set({ teamMembers: [] });
  },

  clearCreateTeamError: () => {
    set({ createTeamError: null });
  },

  clearJoinTeamError: () => {
    set({ joinTeamError: null });
  },
}));
